/**
 * The pillars for the signed-in teacher, derived from the same data the roster uses.
 *
 * Both `StaffLayout` (which draws the menu) and `StaffRoster` (which draws the tabs within
 * it) need to agree on exactly which groups exist today. Deriving that twice from separate
 * queries is how a menu ends up offering a tab the page cannot render, so the derivation
 * lives here and both callers share it — the underlying queries are React Query cached, so
 * the layout costs no extra round trip on top of what the roster already fetches.
 */

import { useMemo } from 'react';
import { buildRosterGroups } from './rosterGroups';
import { buildPillars, type Pillar } from './staffPillars';
import { useAfterSchoolProgramsOn, useTodaysPrograms } from './useTodaysPrograms';

interface StaffUserScope {
  program_ids: string[] | null;
  virtual_class_keys?: string[] | null;
}

export function useStaffPillars(staffUser: StaffUserScope): {
  pillars: Pillar[];
  isLoading: boolean;
} {
  const { data: todaysPrograms = [], isLoading, sessionDate } = useTodaysPrograms(staffUser);
  const holdsVirtual = (staffUser.virtual_class_keys?.length ?? 0) > 0;
  const { data: afterSchoolSeats = [] } = useAfterSchoolProgramsOn(holdsVirtual, sessionDate);
  const virtualKeys = staffUser.virtual_class_keys ?? null;

  const pillars = useMemo(
    () => buildPillars(buildRosterGroups(todaysPrograms, virtualKeys, afterSchoolSeats)),
    [todaysPrograms, virtualKeys, afterSchoolSeats],
  );

  return { pillars, isLoading };
}
